import React, { useState, useEffect } from 'react';
import campaignService from '../../../services/campaignService';
import { useAuth } from '../../../hooks/useAuth';
import Icon from '../../../components/AppIcon';
import UserSelectionModal from './UserSelectionModal';
import { getAllTemplates, getTemplateById, replaceTemplateVariables } from '../../../constants/emailTemplates';

/**
 * Campaign Creation Form Component
 * Modal form for creating and editing campaigns
 */
const CampaignCreationForm = ({ campaign, onClose }) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    templateId: 'welcome_promotion',
    content: {},
    scheduledAt: '',
    filters: {
      role: 'all',
      createdAfter: '',
      createdBefore: ''
    }
  });
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [showUserSelection, setShowUserSelection] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1); // 1: Details, 2: Recipients, 3: Review
  const [testEmail, setTestEmail] = useState('');
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailSuccess, setTestEmailSuccess] = useState(false);

  const templates = getAllTemplates();
  const selectedTemplate = getTemplateById(formData.templateId);

  useEffect(() => {
    if (campaign) {
      setFormData({
        title: campaign.title,
        subject: campaign.subject,
        templateId: campaign.template_id,
        content: campaign.content || {},
        scheduledAt: campaign.scheduled_at ? new Date(campaign.scheduled_at).toISOString().slice(0, 16) : '',
        filters: campaign.filters || { role: 'all', createdAfter: '', createdBefore: '' }
      });
    }
  }, [campaign]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleContentChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      content: { ...prev.content, [field]: value }
    }));
  };

  const handleFilterChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      filters: { ...prev.filters, [field]: value }
    }));
  };

  const handleTemplateChange = (templateId) => {
    const template = getTemplateById(templateId);
    setFormData(prev => ({
      ...prev,
      templateId,
      subject: template?.subject || prev.subject,
      content: {} // Reset content when template changes
    }));
  };

  const handleSelectRecipients = () => {
    setShowUserSelection(true);
  };

  const handleRecipientsSelected = (recipients) => {
    setSelectedRecipients(recipients);
    setShowUserSelection(false);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      setError('Please enter a test email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setTestEmailLoading(true);
    setError('');
    setTestEmailSuccess(false);

    try {
      const result = await campaignService.sendTestEmail({
        email: testEmail,
        subject: formData.subject,
        templateId: formData.templateId,
        content: formData.content
      });

      if (result.success) {
        setTestEmailSuccess(true);
        setTimeout(() => setTestEmailSuccess(false), 5000);
      } else {
        setError(result.error || 'Failed to send test email');
      }
    } catch (err) {
      setError('Failed to send test email: ' + err.message);
    } finally {
      setTestEmailLoading(false);
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Campaign title is required');
      return false;
    }
    if (!formData.subject.trim()) {
      setError('Email subject is required');
      return false;
    }
    if (selectedRecipients.length === 0) {
      setError('Please select at least one recipient');
      return false;
    }

    // Validate template-specific required fields
    if (selectedTemplate) {
      const requiredVars = selectedTemplate.variables.filter(v => v !== 'firstName' && v !== 'appUrl');
      for (const varName of requiredVars) {
        if (!formData.content[varName]) {
          setError(`Please fill in the required field: ${varName}`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create campaign
      const campaignData = {
        title: formData.title,
        subject: formData.subject,
        templateId: formData.templateId,
        content: formData.content,
        createdBy: userProfile.id,
        scheduledAt: formData.scheduledAt || null,
        filters: formData.filters
      };

      const result = await campaignService.createCampaign(campaignData);

      if (!result.success) {
        throw new Error(result.error);
      }

      // Add recipients to campaign
      const recipientIds = selectedRecipients.map(r => r.id);
      const recipientsResult = await campaignService.addRecipientsToCampaign(result.data.id, recipientIds);

      if (!recipientsResult.success) {
        throw new Error(recipientsResult.error);
      }

      // If scheduled, update the campaign status
      if (formData.scheduledAt) {
        await campaignService.scheduleCampaign(result.data.id, formData.scheduledAt);
      }

      onClose(true); // Close and refresh
    } catch (err) {
      setError(err.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => {
    if (!selectedTemplate) return null;

    const previewVars = {
      firstName: 'John',
      appUrl: window.location.origin,
      ...formData.content
    };

    const htmlContent = replaceTemplateVariables(selectedTemplate.htmlTemplate, previewVars);

    return (
      <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 max-h-96 overflow-y-auto">
        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
      </div>
    );
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Campaign Title *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder="e.g., Welcome Campaign 2025"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email Template *
        </label>
        <select
          value={formData.templateId}
          onChange={(e) => handleTemplateChange(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          {templates.map(template => (
            <option key={template.id} value={template.id}>
              {template.name} - {template.description}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email Subject *
        </label>
        <input
          type="text"
          value={formData.subject}
          onChange={(e) => handleInputChange('subject', e.target.value)}
          placeholder="e.g., Welcome to VA Sprouts!"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          required
        />
      </div>

      {selectedTemplate && selectedTemplate.variables.filter(v => v !== 'firstName' && v !== 'appUrl').map(varName => (
        <div key={varName}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {varName.replace(/([A-Z])/g, ' $1').trim()} *
          </label>
          <textarea
            value={formData.content[varName] || ''}
            onChange={(e) => handleContentChange(varName, e.target.value)}
            placeholder={`Enter ${varName}...`}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      ))}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Schedule Send (Optional)
        </label>
        <input
          type="datetime-local"
          value={formData.scheduledAt}
          onChange={(e) => handleInputChange('scheduledAt', e.target.value)}
          min={new Date().toISOString().slice(0, 16)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        />
        <p className="text-xs text-gray-500 mt-1">
          Leave empty to save as draft. Campaign will be sent automatically at the scheduled time.
        </p>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-2 text-primary hover:underline"
        >
          <Icon name={showPreview ? "EyeOff" : "Eye"} size={16} />
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </button>
        {showPreview && (
          <div className="mt-4">
            {renderPreview()}
          </div>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Recipients</h3>
        <p className="text-sm text-gray-600 mb-4">
          Only users who have opted in to marketing emails will be shown.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Role
            </label>
            <select
              value={formData.filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Roles</option>
              <option value="va">Virtual Assistants</option>
              <option value="employer">Employers</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Joined After
            </label>
            <input
              type="date"
              value={formData.filters.createdAfter}
              onChange={(e) => handleFilterChange('createdAfter', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Joined Before
            </label>
            <input
              type="date"
              value={formData.filters.createdBefore}
              onChange={(e) => handleFilterChange('createdBefore', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={handleSelectRecipients}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Icon name="Users" size={18} />
          Select Recipients ({selectedRecipients.length} selected)
        </button>
      </div>

      {selectedRecipients.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Selected Recipients ({selectedRecipients.length})
          </h4>
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
            {selectedRecipients.map(recipient => (
              <div key={recipient.id} className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded">
                <div>
                  <p className="text-sm font-medium text-gray-900">{recipient.full_name}</p>
                  <p className="text-xs text-gray-500">{recipient.email}</p>
                </div>
                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                  {recipient.role}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Campaign</h3>
      
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div>
          <span className="text-sm font-medium text-gray-700">Title:</span>
          <p className="text-gray-900">{formData.title}</p>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-700">Subject:</span>
          <p className="text-gray-900">{formData.subject}</p>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-700">Template:</span>
          <p className="text-gray-900">{selectedTemplate?.name}</p>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-700">Recipients:</span>
          <p className="text-gray-900">{selectedRecipients.length} users</p>
        </div>
        {formData.scheduledAt && (
          <div>
            <span className="text-sm font-medium text-gray-700">Scheduled:</span>
            <p className="text-gray-900">{new Date(formData.scheduledAt).toLocaleString()}</p>
          </div>
        )}
      </div>

      {/* Test Email Section */}
      <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Icon name="Mail" size={18} className="text-blue-600" />
          Send Test Email
        </h4>
        <p className="text-xs text-gray-600 mb-3">
          Send a test email to verify how your campaign looks before sending to all recipients.
        </p>
        <div className="flex gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => {
              setTestEmail(e.target.value);
              setError('');
              setTestEmailSuccess(false);
            }}
            placeholder="Enter test email address"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
          <button
            type="button"
            onClick={handleSendTestEmail}
            disabled={testEmailLoading || !testEmail.trim()}
            className="px-4 py-2 text-white rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
            style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
          >
            {testEmailLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Sending...
              </>
            ) : (
              <>
                <Icon name="Send" size={16} />
                Send Test
              </>
            )}
          </button>
        </div>
        {testEmailSuccess && (
          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded flex items-center gap-2">
            <Icon name="CheckCircle" size={16} className="text-green-600" />
            <p className="text-xs text-green-800">Test email sent successfully!</p>
          </div>
        )}
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Email Preview:</h4>
        {renderPreview()}
      </div>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {campaign ? 'Edit Campaign' : 'Create New Campaign'}
            </h2>
            <button
              onClick={() => onClose(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 bg-gray-50 border-b">
            <div className="flex items-center justify-between max-w-2xl mx-auto">
              {['Details', 'Recipients', 'Review'].map((step, index) => (
                <React.Fragment key={step}>
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      currentStep > index + 1 ? 'bg-green-500 text-white' :
                      currentStep === index + 1 ? 'bg-primary text-white' :
                      'bg-gray-300 text-gray-600'
                    }`}>
                      {currentStep > index + 1 ? <Icon name="Check" size={16} /> : index + 1}
                    </div>
                    <span className={`ml-2 text-sm font-medium ${
                      currentStep === index + 1 ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {step}
                    </span>
                  </div>
                  {index < 2 && (
                    <div className={`flex-1 h-1 mx-4 ${
                      currentStep > index + 1 ? 'bg-green-500' : 'bg-gray-300'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-6">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="mt-6 flex justify-between">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                if (currentStep > 1) {
                  setCurrentStep(currentStep - 1);
                } else {
                  onClose(false);
                }
              }}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {currentStep === 1 ? 'Cancel' : 'Back'}
            </button>
            
            <div className="flex gap-3">
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentStep(currentStep + 1);
                  }}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                  {loading ? 'Creating...' : (formData.scheduledAt ? 'Schedule Campaign' : 'Save as Draft')}
                </button>
              )}
            </div>
          </div>
            </div>
          </form>
        </div>
      </div>

      {/* User Selection Modal */}
      {showUserSelection && (
        <div onClick={(e) => e.stopPropagation()}>
          <UserSelectionModal
            filters={formData.filters}
            selectedUsers={selectedRecipients}
            onClose={() => setShowUserSelection(false)}
            onConfirm={handleRecipientsSelected}
          />
        </div>
      )}
    </>
  );
};

export default CampaignCreationForm;
